import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import DataTable from "react-data-table-component";
import PageWrapper from "@components/layouts/PageWrapper";
import Search from "@components/layouts/Search";
import { useLocation, useNavigate } from "react-router-dom";
import usePagination from "@hooks/usePagination";
import useSearch from "@hooks/useSearch";
import { useErrorHandling } from "@hooks/useErrorHandling";
import { castTypeArr } from "@utils/general/cast-type";
import { TFailResponse } from "@type-defs/general/TFailResponse";
import { DeletePopup } from "@helpers/popups/DeletePopup";
import TableSkeleton from "@components/layouts/TableSkeleton";
import { TDeleteSuccesResponse } from "@type-defs/general/TDeleteSuccessResponse";
import { Toast } from "@helpers/popups/Toast";
import {
  useDeletePrimarySaleOrderMutation,
  useGetAllPrimarySaleOrdersQuery,
} from "@states/primary-sale-order/primary-sale-order.api";
// import { secondary_SALE_ORDER } from '@utils/constants/primary-sale-order.constants';
import { saleOrderColumns } from "@helpers/columns/primary-sale-order/sale-order.columns";
import CollaspseIcon from "@components/icons/CollaspseIcon";
import ExpandIcon from "@components/icons/ExpandIcon";
import NoRecord from "@components/layouts/NoRecord";
import OutlinedStatus from "@components/status/OutlinedStatus";
import SalesOrderFilter from "@components/filters/SalesOrderFilter";
import {
  useDeleteSecondarySaleOrderMutation,
  useGetAllSecondarySaleOrdersQuery,
  useLazyGetSecondarySaleOrderQuery,
} from "@states/secondary-sale-order/secondary-sale-order.api";
import { SECONDARY_SALE_ORDER } from "@utils/constants/secondary-sale-order.constant";
import { PRIMARY_SALE_ORDER } from "@utils/constants/primary-sale-order.constants";
import SaleOrderDetailModal from "@components/modals/sales-order/SaleOrderDetailModal";
import { LoadingSpinner } from "@components/layouts/LoadingSpinner";
import InvoiceDetailModal from "@components/modals/sales-order/InvoiceDetailModal";
import { useLazyGetSecondaryInvoiceQuery } from "@states/secondary-sale-order/secondary-invoice.api";
import PaymentReceiveModal from "@components/modals/sales-order/PaymentReceiveModal";
import { useLazyGetSecondaryPaymentReceivveQuery } from "@states/secondary-sale-order/secondary-payment-receive.api";
import { formatDBDate } from "@utils/general/format-db-date";
import AuthComponent from "@components/auth/component";
import useGetPermission from "@hooks/common/useGetPermission";
import {
  useGetGoodsRequestQuery,
  useLazyGetOneGoodsRequestQuery,
} from "@states/goods-request/goods-request.api";
import GoodRequestDetailModal from "@components/modals/sales-order/GoodIssueDetailModal";
import { useLazyGetGoodsIssueQuery } from "@states/goods-request/goods-issue.api";
import GoodIssueDetailModal from "@components/modals/sales-order/GoodIssueDetailModal";
import { useLazyGetGoodsDeliveryQuery } from "@states/goods-request/delivery-note.api";
import DeliveryDetailModal from "@components/modals/sales-order/DeliveryDetailModal";

const SecondarySalesOrdersPage = () => {
  const { hasAccess } = useGetPermission();
  const { page, limit, handlePageChange, handlePerRowsChange } =
    usePagination();
  const [isOpen, setIsOpen] = React.useState(false);
  const [isInvoiceOpen, setIsInvoiceOpen] = React.useState(false);
  const [isPaymentReceiveOpen, setIsPaymentReceiveOpen] = React.useState(false);
  const [isGoodRequestOpen, setIsGoodRequestOpen] = React.useState(false);
  const [isGoodIssueOpen, setIsGoodIssueOpen] = React.useState(false);
  const [isDeliveryOpen, setIsDeliveryOpen] = React.useState(false);
  const { search, setSearch } = useSearch();
  const tarRef = useRef<any>(null);
  const filterRef = useRef({
    from_date: undefined,
    to_date: undefined,
    customer_id: undefined,
    status: undefined,
    payment_type_id: undefined,
  });

  const navigate = useNavigate();
  const location = useLocation();
  const [expandedRowIds, setExpandedRowIds] = React.useState<
    number[] | string[] | any[]
  >([]);

  const {
    data,
    isFetching,
    error: getAllError,
    refetch,
  } = useGetAllSecondarySaleOrdersQuery({
    page,
    limit,
    type: "Secondary",
    customer_name: search,
    ...filterRef.current,
  });
  const [deltePrimarySaleOrder, { error: deleteError, isLoading }] =
    useDeleteSecondarySaleOrderMutation();

  const [
    getSecondarySaleOrder,
    {
      data: getSecondarySaleOrderData,
      isLoading: getSecondarySaleOrderLoading,
    },
  ] = useLazyGetSecondarySaleOrderQuery();

  const [
    getSecondayInvoice,
    { data: getSecondaryInvoiceData, isLoading: getSecondaryInvoiceLoading },
  ] = useLazyGetSecondaryInvoiceQuery();

  const [
    getPaymentReceive,
    { data: getPaymentReceiveData, isLoading: getPaymentReceiveLoading },
  ] = useLazyGetSecondaryPaymentReceivveQuery();

  const [
    getOneGoodsRequest,
    { data: getOneGoodsRequestData, isLoading: getOneGoodsRequestDataLoading },
  ] = useLazyGetOneGoodsRequestQuery();

  const [
    getGoodsIssue,
    { data: getGoodsIssueData, isLoading: getGoodsIssueDataLoading },
  ] = useLazyGetGoodsIssueQuery();

  const [
    getGoodsDelivery,
    { data: getGoodsDeliveryData, isLoading: getGoodsDeliveryDataLoading },
  ] = useLazyGetGoodsDeliveryQuery();

  useErrorHandling(...castTypeArr<TFailResponse>([getAllError, deleteError]));

  useLayoutEffect(() => {
    // Select all elements with the class 'my-class'
    const elements = document.getElementsByClassName("wtf");

    // Check if there are any elements and get the last one
    if (elements.length > 0) {
      const lastElement = elements[elements.length - 1];

      if (lastElement) {
        tarRef.current = lastElement;

        tarRef.current.classList.add("another-class");
      }

      // Add the new class to the last element
      // lastElement.classList.add('another-class');
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, []);

  useEffect(() => {
    if (location.state?.reload) {
      refetch();
    }
  }, [location.state?.reload]);

  const okayData = useMemo(
    () =>
      //@ts-ignore
      data?.data?.map((d, i) => ({
        ...d,
        no: i,
      })),
    [data]
  );

  const editHandler = (
    secondary_sales_order_id: string | number,
    status: string | number,
    payment_type_id: string | number
  ) => {
    navigate(
      `${SECONDARY_SALE_ORDER.BASE_PATH}${secondary_sales_order_id}/edit`,
      {
        state: {
          status,
          payment_type_id,
        },
      }
    );
  };

  const deleteHandler = async (id: number | string) => {
    try {
      const { isConfirmed } = await DeletePopup.fire();

      if (isConfirmed) {
        const res = await deltePrimarySaleOrder(id);

        const successData = "data" in res ? res.data : null;
        const { success, message } = successData as TDeleteSuccesResponse;

        if (success) {
          Toast.fire({
            icon: "success",
            title: message,
          });

          refetch();
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleOrderApprovalModalOpen = (id: string | number) => {
    getSecondarySaleOrder({
      secondary_sales_order_id: id,
    }).then((res) => {
      if (res.data) {
        setIsOpen(true);
      }
    });
  };

  const handleInvoiceModalOpen = (id: string | number) => {
    getSecondayInvoice({
      secondary_sales_invoice_id: id,
    }).then((res) => {
      if (res.data) {
        setIsInvoiceOpen(true);
      }
    });
  };

  const handlePaymentReceiveModalOpen = (id: string | number) => {
    getPaymentReceive({
      secondary_sales_payment_receive_id: id,
    }).then((res) => {
      if (res.data) {
        setIsPaymentReceiveOpen(true);
      }
    });
  };

  const handleGoodRequestModalOpen = (id: string | number) => {
    getOneGoodsRequest({
      goods_request_id: id,
    }).then((res) => {
      if (res.data) {
        setIsGoodRequestOpen(true);
      }
    });
  };

  const handleGoodIssueModalOpen = (id: string | number) => {
    getGoodsIssue({
      goods_issue_id: id,
    }).then((res) => {
      if (res.data) {
        setIsGoodIssueOpen(true);
      }
    });
  };

  const handleDeliveryNoteModalOpen = (id: string | number) => {
    getGoodsDelivery({
      goods_delivery_id: id,
    }).then((res) => {
      if (res.data) {
        setIsDeliveryOpen(true);
      }
    });
  };

  // alert('hit')
  console.log("okayData", okayData);
  return (
    <AuthComponent
      action="view"
      module="Secondary Sale Order"
      subModule="Secondary Sale Order"
      page
    >
      {(getSecondarySaleOrderLoading ||
        getSecondaryInvoiceLoading ||
        getGoodsDeliveryDataLoading ||
        getGoodsDeliveryDataLoading ||
        getGoodsIssueDataLoading ||
        getOneGoodsRequestDataLoading) && <LoadingSpinner />}
      <Search setSearch={setSearch} />
      <SaleOrderDetailModal
        //@ts-ignore
        data={getSecondarySaleOrderData?.data || {}}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
      <InvoiceDetailModal
        //@ts-ignore
        data={getSecondaryInvoiceData?.data || {}}
        isOpen={isInvoiceOpen}
        setIsOpen={setIsInvoiceOpen}
      />
      <PaymentReceiveModal
        //@ts-ignore
        data={getPaymentReceiveData?.data || {}}
        isOpen={isPaymentReceiveOpen}
        setIsOpen={setIsPaymentReceiveOpen}
      />
      <GoodRequestDetailModal
        //@ts-ignore
        data={getOneGoodsRequestData?.data || {}}
        isOpen={isGoodRequestOpen}
        setIsOpen={setIsGoodRequestOpen}
      />
      <GoodIssueDetailModal
        //@ts-ignore
        data={getGoodsIssueData?.data || {}}
        isOpen={isGoodIssueOpen}
        setIsOpen={setIsGoodIssueOpen}
      />
      <DeliveryDetailModal
        //@ts-ignore
        data={getGoodsDeliveryData?.data || {}}
        isOpen={isDeliveryOpen}
        setIsOpen={setIsDeliveryOpen}
      />
      <PageWrapper
        addPath={SECONDARY_SALE_ORDER.ADD_PATH()}
        addButtonTitle={SECONDARY_SALE_ORDER.ADD_BUTTON_TITLE}
        title={SECONDARY_SALE_ORDER.LIST_PAGE_TITLE}
        hasCreatePermission={hasAccess("create", "Secondary Sale Order", "Secondary Sale Order")}
      >
        <SalesOrderFilter refetch={refetch} filterRef={filterRef} />
        <DataTable
          keyField="secondary_sales_order_id"
          responsive
          striped
          pagination
          paginationServer
          //@ts-ignore
          paginationTotalRows={data?.paginator?.totalItems}
          progressPending={isFetching}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          noDataComponent={<NoRecord />}
          progressComponent={<TableSkeleton row={10} col={8} />}
          //@ts-ignore
          columns={saleOrderColumns(editHandler, deleteHandler)}
          //@ts-ignore
          data={[
            ...(okayData ?? []),
            {
              no: "Totals:",
              grand_total_amount:
                (okayData &&
                  okayData[0] &&
                  okayData?.[0]["grand_total_sum"]?.toFixed(2)) ||
                0,
              noAction: true,
            },
          ]}
          // data={data?.data}
          conditionalRowStyles={[
            {
              when: (row) =>
                expandedRowIds.includes(row.secondary_sales_order_id),
              style: {
                backgroundColor: "#D3E4F8",
                // color: 'white',
                "&:hover": {
                  cursor: "pointer",
                },
              },
            },
            {
              when: (row) => row.no === "Totals:",
              style: {
                backgroundColor: "#F1F2F5",
                fontWeight: 500,
              },
            },
          ]}
          onRowExpandToggled={(expanded, row) => {
            setExpandedRowIds((prev) => {
              if (expanded && !prev.includes(row.secondary_sales_order_id)) {
                return [...prev, row.secondary_sales_order_id];
              } else {
                return prev.filter((id) => id !== row.secondary_sales_order_id);
              }
            });
            return <div>A</div>;
          }}
          expandableRows
          expandableRowDisabled={(row) =>
            (row.invoice_data === null &&
              row.payment_receive_data === null &&
              row.order_approval_data === null &&
              row.goods_request_note_data === null &&
              row.goods_issue_note_data === null) ||
            row.no === "Totals:"
          }
          expandableInheritConditionalStyles
          expandableIcon={{
            collapsed: <CollaspseIcon />,
            expanded: <ExpandIcon />,
          }}
          expandableRowsComponent={(row) => {
            return (
              <>
                {/* Order Approval */}
                {/* @ts-ignore */}
                {row.data?.order_approval_data?.map(
                  //   @ts-ignore
                  (orderApproval, index) => (
                    <div className="grid border-[0.1px] border-b-gray-200 w-full h-full pl-[8%] min-w-full grid-cols-6 p-[0.59rem] text-primary-dark bg-gray-100">
                      <div className="flex items-center justify-center">
                        {index + 1}
                      </div>
                      <div className="flex items-center font-semibold">
                        Order Approval
                      </div>
                      <div
                        onClick={() =>
                          handleOrderApprovalModalOpen(
                            orderApproval.secondary_sales_order_id
                          )
                        }
                        className="flex items-center italic font-semibold underline"
                      >
                        {orderApproval.secondary_sales_order_code}
                      </div>
                      <div className="flex items-center">
                        {/* {orderApproval.sales_date} */}
                        {orderApproval.created_time}
                      </div>
                      <div className="flex items-center">
                        <OutlinedStatus
                          width={"w-fit min-w-[11ch]"}
                          title={orderApproval.status}
                          color={
                            orderApproval.status === "Invoice Created"
                              ? "green"
                              : orderApproval.status === "Open"
                              ? "blue"
                              : "gray"
                          }
                        />
                      </div>
                    </div>
                  )
                )}
                {/* Invoices */}
                {/* @ts-ignore */}
                {row.data?.invoice_data?.map(
                  //   @ts-ignore
                  (invoice, index) => (
                    <div className="grid border-[0.1px] border-b-gray-200 w-full h-full pl-[8%] min-w-full grid-cols-6 p-[0.59rem] text-primary-dark bg-gray-100">
                      <div className="flex items-center justify-center">
                        {index + 1}
                      </div>
                      <div className="flex items-center font-semibold">
                        Invoice
                      </div>
                      <div
                        onClick={() =>
                          handleInvoiceModalOpen(
                            invoice.secondary_sales_invoice_id
                          )
                        }
                        className="flex items-center italic font-semibold underline"
                      >
                        {invoice.secondary_sales_invoice_code}
                      </div>
                      <div className="flex items-center">
                        {invoice.invoice_date}
                      </div>
                      <div className="flex items-center">
                        <OutlinedStatus
                          width={"w-fit min-w-[11ch]"}
                          title={invoice.status}
                          color={
                            invoice.status === "Paid"
                              ? "green"
                              : invoice.status === "Partial Paid"
                              ? "purple"
                              : invoice.status === "Open"
                              ? "blue"
                              : "gray"
                          }
                        />
                      </div>
                    </div>
                  )
                )}
                {/* Payment Receive */}
                {/* @ts-ignore */}
                {row.data?.payment_receive_data?.map(
                  //   @ts-ignore
                  (paymentReceive, index) => (
                    <div className="grid w-full h-full pl-[8%] min-w-full grid-cols-6 p-[0.89rem] text-primary-dark bg-gray-100">
                      <div className="flex items-center justify-center">
                        {index + 1}
                      </div>
                      <div className="flex items-center font-semibold">
                        Payment Receive
                      </div>
                      <div
                        onClick={() =>
                          handlePaymentReceiveModalOpen(
                            paymentReceive.secondary_sales_payment_receive_id
                          )
                        }
                        className="flex items-center italic font-semibold underline "
                      >
                        {paymentReceive.payment_receive_code}
                      </div>
                      <div className="flex items-center">
                        {paymentReceive.payment_receive_date}
                      </div>
                      <div className="flex items-center">-</div>
                    </div>
                  )
                )}
                {/* Goods Request Note */}
                {/* @ts-ignore */}
                {row.data?.goods_request_note_data?.map(
                  //   @ts-ignore
                  (goodsRequestNote, index) => {
                    return (
                      <div className="grid border-[0.1px] border-b-gray-200 w-full h-full pl-[8%] min-w-full grid-cols-6 p-[0.59rem] text-primary-dark bg-gray-100">
                        <div className="flex items-center justify-center">
                          {index + 1}
                        </div>
                        <div className="flex items-center font-semibold">
                          Goods Request
                        </div>
                        <div
                          onClick={() =>
                            handleGoodRequestModalOpen(
                              goodsRequestNote.goods_request_note_id
                            )
                          }
                          className="flex items-center italic font-semibold underline"
                        >
                          {goodsRequestNote.goods_request_note_code}
                        </div>
                        <div className="flex items-center">
                          {goodsRequestNote.request_date}
                        </div>
                        <div className="flex items-center">
                          <OutlinedStatus
                            width={"w-fit min-w-[11ch]"}
                            title={goodsRequestNote.status}
                            color={
                              goodsRequestNote.status === "Issued"
                                ? "green"
                                : goodsRequestNote.status === "Partial Issued"
                                ? "purple"
                                : goodsRequestNote.status === "Open"
                                ? "blue"
                                : "gray"
                            }
                          />
                        </div>
                      </div>
                    );
                  }
                )}
                {/* Goods Return Note */}
                {/* @ts-ignore */}
                {row.data?.goods_return_note_data?.map(
                  //   @ts-ignore
                  (goodsRequestNote, index) => (
                    <div className="grid border-[0.1px] border-b-gray-200 w-full h-full pl-[8%] min-w-full grid-cols-6 p-[0.59rem] text-primary-dark bg-gray-100">
                      <div className="flex items-center justify-center">
                        {index + 1}
                      </div>
                      <div className="flex items-center font-semibold">
                        Goods Return
                      </div>
                      <div className="flex items-center italic font-semibold underline">
                        {goodsRequestNote.goods_return_note_code}
                      </div>
                      <div className="flex items-center">
                        {goodsRequestNote.return_date}
                      </div>
                      <div className="flex items-center">
                        <OutlinedStatus
                          width={"w-fit min-w-[11ch]"}
                          title={goodsRequestNote.status}
                          color={
                            goodsRequestNote.status === "Received"
                              ? "green"
                              : goodsRequestNote.status === "Partial Received"
                              ? "purple"
                              : goodsRequestNote.status === "Open"
                              ? "blue"
                              : "gray"
                          }
                        />
                      </div>
                    </div>
                  )
                )}

                {/* Goods Receive Note */}
                {/* @ts-ignore */}
                {row.data?.goods_receive_data?.map(
                  //   @ts-ignore
                  (goodsRequestNote, index) => (
                    <div className="grid border-[0.1px] border-b-gray-200 w-full h-full pl-[8%] min-w-full grid-cols-6 p-[0.59rem] text-primary-dark bg-gray-100">
                      <div className="flex items-center justify-center">
                        {index + 1}
                      </div>
                      <div className="flex items-center font-semibold">
                        Goods Receive
                      </div>
                      <div className="flex items-center italic font-semibold underline">
                        {goodsRequestNote.goods_receive_note_code}
                      </div>
                      <div className="flex items-center">
                        {goodsRequestNote.receive_date}
                      </div>
                      <div className="flex items-center">
                        <OutlinedStatus
                          width={"w-fit min-w-[11ch]"}
                          title={goodsRequestNote.status}
                          color={
                            goodsRequestNote.status === "Received"
                              ? "green"
                              : goodsRequestNote.status === "Partial Received"
                              ? "purple"
                              : goodsRequestNote.status === "Open"
                              ? "blue"
                              : "gray"
                          }
                        />
                      </div>
                    </div>
                  )
                )}
                {/* Goods Issue Note */}
                {/* @ts-ignore */}
                {row.data?.goods_issue_note_data?.map(
                  //   @ts-ignore
                  (goodIssueNote, index) => {
                    console.log("goodIssueNote", goodIssueNote);
                    return (
                      <div className="grid border-[0.1px] border-b-gray-200 w-full h-full pl-[8%] min-w-full grid-cols-6 p-[0.59rem] text-primary-dark bg-gray-100">
                        <div className="flex items-center justify-center">
                          {index + 1}
                        </div>
                        <div className="flex items-center font-semibold">
                          Goods Issue
                        </div>
                        <div
                          onClick={() =>
                            handleGoodIssueModalOpen(
                              goodIssueNote.goods_issue_note_id
                            )
                          }
                          className="flex items-center italic font-semibold underline"
                        >
                          {goodIssueNote.goods_issue_note_code}
                        </div>
                        <div className="flex items-center">
                          {goodIssueNote.issue_date}
                        </div>
                        <div className="flex items-center">
                          <OutlinedStatus
                            width={"w-fit min-w-[11ch]"}
                            title={goodIssueNote.status}
                            color={
                              goodIssueNote.status === "Issued"
                                ? "green"
                                : goodIssueNote.status === "Partial Issued"
                                ? "purple"
                                : goodIssueNote.status === "Open"
                                ? "blue"
                                : "gray"
                            }
                          />
                        </div>
                      </div>
                    );
                  }
                )}

                {/* Goods Issue Note */}
                {/* @ts-ignore */}
                {row.data?.delivery_note_data?.map(
                  //   @ts-ignore
                  (goodIssueNote, index) => (
                    <div className="grid border-[0.1px] border-b-gray-200 w-full h-full pl-[8%] min-w-full grid-cols-6 p-[0.59rem] text-primary-dark bg-gray-100">
                      <div className="flex items-center justify-center">
                        {index + 1}
                      </div>
                      <div className="flex items-center font-semibold">
                        Delivery Note
                      </div>
                      <div
                        onClick={() =>
                          handleDeliveryNoteModalOpen(
                            goodIssueNote.goods_delivery_note_id
                          )
                        }
                        className="flex items-center italic font-semibold underline"
                      >
                        {goodIssueNote.goods_delivery_note_code}
                      </div>
                      <div className="flex items-center">
                        {goodIssueNote.delivery_note_date}
                      </div>
                      <div className="flex items-center">
                        <OutlinedStatus
                          width={"w-fit min-w-[11ch]"}
                          title={goodIssueNote.status}
                          color={
                            goodIssueNote.status === "Delivered"
                              ? "green"
                              : goodIssueNote.status === "Pending"
                              ? "blue"
                              : "gray"
                          }
                        />
                      </div>
                    </div>
                  )
                )}
              </>
            );
          }}
        />
      </PageWrapper>
    </AuthComponent>
  );
};

export default SecondarySalesOrdersPage;
